import * as THREE from 'three';
import type { PlayerControl } from '../sim/contracts';

export interface CameraFocus {
  focusPlayer(): void;
  toggleFollowPlayer(): void;
  toggleAntCam(): void;
  goHome(): void;
  toggleKeyHelp(): void;
}

// Click-vs-drag threshold. OrbitControls consumes left-drag for rotate, so we
// can't attach to `click` directly (it fires after drag too). Instead we gate
// on total pointer travel — 4px is large enough to ignore finger jitter on
// touch devices but small enough that a deliberate click registers.
const CLICK_MAX_TRAVEL_PX = 4;

export class Input {
  private readonly dom: HTMLCanvasElement;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly terrainMesh: THREE.Mesh;
  private readonly control: PlayerControl;
  private readonly clickableRoots: THREE.Object3D[] = [];
  /** Optional pebble interaction hook. Pointer-up first raycasts against
   *  the pebble mesh; if it hits and the handler returns true, the click
   *  is consumed (pickup/drop). Otherwise it falls through to
   *  setPlayerGoal. Also called with the ground point if the player is
   *  carrying a pebble, so clicks anywhere drop it there. */
  private pebbleRoot: THREE.Object3D | null = null;
  private pebbleClick: ((worldX: number, worldZ: number, hitPebble: boolean, instanceId: number | null, isDouble: boolean) => boolean) | null = null;
  setPebbleHandler(
    root: THREE.Object3D,
    onClick: (x: number, z: number, hitPebble: boolean, instanceId: number | null, isDouble: boolean) => boolean,
  ): void {
    this.pebbleRoot = root;
    this.pebbleClick = onClick;
  }

  // Double-click detection — two pointerups within DBL_MS of each other AND
  // within DBL_PX of the previous location register as a double-click.
  private lastClickT = 0;
  private lastClickX = 0;
  private lastClickY = 0;
  private static readonly DBL_MS = 350;
  private static readonly DBL_PX = 12;

  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();

  private downX = 0;
  private downY = 0;
  private downValid = false;

  private readonly focus: CameraFocus | null;

  constructor(
    dom: HTMLCanvasElement,
    camera: THREE.PerspectiveCamera,
    terrainMesh: THREE.Mesh,
    control: PlayerControl,
    focus?: CameraFocus,
  ) {
    this.dom = dom;
    this.camera = camera;
    this.terrainMesh = terrainMesh;
    this.control = control;
    this.focus = focus ?? null;

    this.dom.addEventListener('pointerdown', this.onPointerDown);
    this.dom.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('keydown', this.onKeyDown);
  }

  dispose(): void {
    this.dom.removeEventListener('pointerdown', this.onPointerDown);
    this.dom.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  /** Register an additional object the raycaster should test against. Use for
   *  rocks / grass groups so clicks on obstructions don't tunnel through to
   *  the terrain far behind them. */
  addClickable(o: THREE.Object3D): void {
    this.clickableRoots.push(o);
  }

  /** Optional override: when present AND returns true, the pointerup handler
   *  skips surface logic and hands the click to the owner instead. Used for
   *  the underground-nest mode. */
  private onClickOverride: ((p: THREE.Vector3, exitHit: boolean) => void) | null = null;
  private overrideGetState: (() => {
    active: boolean;
    walkables: THREE.Mesh[];
    exitPos: { x: number; y: number; z: number } | null;
  }) | null = null;
  setModeOverride(
    get: () => { active: boolean; walkables: THREE.Mesh[]; exitPos: { x: number; y: number; z: number } | null },
    onClick: (p: THREE.Vector3, exitHit: boolean) => void,
  ): void {
    this.overrideGetState = get;
    this.onClickOverride = onClick;
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) {
      this.downValid = false;
      return;
    }
    this.downValid = true;
    this.downX = e.clientX;
    this.downY = e.clientY;
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.downValid || e.button !== 0) return;
    this.downValid = false;

    const dx = e.clientX - this.downX;
    const dy = e.clientY - this.downY;
    if (dx * dx + dy * dy > CLICK_MAX_TRAVEL_PX * CLICK_MAX_TRAVEL_PX) return;

    const rect = this.dom.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.ndc, this.camera);

    // Underground override: raycast against the interior walkable meshes.
    // If close to the exit ring we treat the click as "ascend"; otherwise it's
    // a move-goal inside the chamber.
    const state = this.overrideGetState?.();
    if (state?.active && this.onClickOverride) {
      const hits = this.raycaster.intersectObjects(state.walkables, false);
      if (hits.length === 0) return;
      const p = hits[0].point;
      const exit = state.exitPos;
      const exitHit = !!exit && (p.x - exit.x) * (p.x - exit.x) + (p.z - exit.z) * (p.z - exit.z) < 1.3 * 1.3;
      this.onClickOverride(p, exitHit);
      return;
    }

    // Double-click detection.
    const now = performance.now();
    const ddx = e.clientX - this.lastClickX;
    const ddy = e.clientY - this.lastClickY;
    const isDouble =
      now - this.lastClickT < Input.DBL_MS &&
      ddx * ddx + ddy * ddy < Input.DBL_PX * Input.DBL_PX;
    this.lastClickT = isDouble ? 0 : now;  // reset after match so triple doesn't chain
    this.lastClickX = e.clientX;
    this.lastClickY = e.clientY;

    // Test the terrain AND every registered obstruction (rocks, grass).
    // intersectObjects returns sorted-by-distance results, so hits[0] is
    // whatever actually faces the cursor — no tunnel-through.
    const roots: THREE.Object3D[] = [this.terrainMesh, ...this.clickableRoots];
    if (this.pebbleRoot) roots.unshift(this.pebbleRoot);
    const hits = this.raycaster.intersectObjects(roots, true);
    if (hits.length === 0) return;
    const first = hits[0];
    const p = first.point;

    // Pebble handler first — single-click a pebble = walk-and-pickup,
    // double-click anywhere while carrying = walk-and-drop.
    if (this.pebbleClick) {
      const hitPebble = !!this.pebbleRoot && first.object.userData.__pebbleMesh === true;
      const instanceId = hitPebble && typeof first.instanceId === 'number' ? first.instanceId : null;
      const consumed = this.pebbleClick(p.x, p.z, hitPebble, instanceId, isDouble);
      if (consumed) return;
    }
    this.control.setPlayerGoal(p.x, p.z);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    const target = e.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
    }

    switch (e.key) {
      case '1':
        this.control.summonFollowers(10);
        break;
      case '2':
        this.control.summonFollowers(30);
        break;
      case '3':
        this.control.releaseFollowers(0.5);
        break;
      case '4':
        this.control.releaseFollowers(1);
        break;
      case 'u':
      case 'U':
        this.control.toggleNestRecall();
        break;
      case 'g':
      case 'G':
        this.focus?.focusPlayer();
        break;
      case 'f':
      case 'F':
        this.focus?.toggleFollowPlayer();
        break;
      case 'v':
      case 'V':
        this.focus?.toggleAntCam();
        break;
      case 'h':
      case 'H':
        this.focus?.goHome();
        break;
      case 'k':
      case 'K':
        this.focus?.toggleKeyHelp();
        break;
      default:
        return;
    }
  };
}
